import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import HMSPageHeader from '../../components/ui/HMSPageHeader';
import HMSTable from '../../components/ui/HMSTable';
import HMSButton from '../../components/ui/HMSButton';
import HMSModal from '../../components/ui/HMSModal';
import { TextInput } from '../../components/ui/fields';
import { useFlashMessage } from '../../components/FlashMessage';
import { extractApiError } from '../../utils/errorMessages';
import { closeBooks, fetchBooksClosed, forgetBooksClosed } from '../../services/booksClosedService';
import './ClosedMonthsPage.css';

/*
 * Closed months: each company's "Books closed up to" month (Chris, 2026-10-17).
 * Finance closes a company's month when its books are done; from then on no
 * invoice of that company can be given that month or an earlier one. Anyone in
 * Financials reads this page; only financial approvers close or reopen, and
 * every change is listed with who and when. The rule lives on the server
 * (invoicing/services/reporting_month.py).
 */

const monthInWords = (yyyyMm) => {
  if (!yyyyMm) return '';
  const [year, month] = yyyyMm.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleString('en-GB', { month: 'long', year: 'numeric' });
};

const whenInWords = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', {
  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '');

// The latest month that has ended: last month, as the month box holds it.
const lastMonthValue = () => {
  const now = new Date();
  const last = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}`;
};

const COLUMNS = [
  { key: 'name', label: 'Company' },
  { key: 'closed_through', label: 'Books closed up to' },
  { key: 'changed_by', label: 'Changed by' },
  { key: 'changed_at', label: 'When' },
  { key: 'actions', label: '', align: 'right' },
];

const ClosedMonthsPage = () => {
  const { showSuccess } = useFlashMessage();
  const [data, setData] = useState({ can_change: false, companies: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [month, setMonth] = useState('');
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setData(await fetchBooksClosed());
    } catch (error) {
      setLoadError(extractApiError(error, "The closed months couldn't be loaded."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openChange = (company) => {
    setEditing(company);
    setMonth(company.closed_through || lastMonthValue());
    setSaveError(null);
  };

  const save = async () => {
    if (!/^\d{4}-\d{2}$/.test(month)) {
      setSaveError('Pick a month.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await closeBooks(editing.id, month);
      forgetBooksClosed();
      showSuccess(`${editing.name}: books closed up to ${monthInWords(month)}.`);
      setEditing(null);
      await load();
    } catch (error) {
      const body = error?.response?.data || {};
      setSaveError(body.closed_through || body.hms_entity || body.detail
        || extractApiError(error, "The month couldn't be saved."));
    } finally {
      setSaving(false);
    }
  };

  const renderCell = (row, column) => {
    switch (column.key) {
      case 'closed_through':
        return row.closed_through
          ? <strong>{monthInWords(row.closed_through)}</strong>
          : <span className="cm-muted">Nothing closed yet</span>;
      case 'changed_at':
        return whenInWords(row.changed_at);
      case 'actions':
        return data.can_change
          ? <HMSButton variant="secondary" onClick={() => openChange(row)}>Change</HMSButton>
          : null;
      default:
        return row[column.key];
    }
  };

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="closed-months-page">
        <HMSPageHeader
          kicker="Finance master data"
          title="Closed months"
          subtitle="Once a company's month is closed, no invoice of that company can be given that month or an earlier one."
        />
        {!data.can_change && !loading && !loadError && (
          <p className="cm-note">Only a financial approver can close or reopen a month.</p>
        )}
        {loadError ? (
          <div className="cm-error" role="alert">
            {loadError}{' '}
            <HMSButton variant="secondary" onClick={load}>Try again</HMSButton>
          </div>
        ) : (
          <HMSTable
            columns={COLUMNS}
            rows={data.companies}
            renderCell={renderCell}
            rowKey={(row) => row.id}
            loading={loading}
            empty={{ title: 'No companies to show', hint: 'No office keeps the books for a company you can see.' }}
            label="Closed months per company"
          />
        )}
      </div>

      <HMSModal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title={editing ? `Books closed up to: ${editing.name}` : ''}
        size="small"
        footer={(
          <>
            <HMSButton variant="secondary" onClick={() => setEditing(null)}>Cancel</HMSButton>
            <HMSButton variant="primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </HMSButton>
          </>
        )}
      >
        <TextInput
          type="month"
          label="Books closed up to and including"
          hint={`Pick an earlier month to reopen. The latest month you can close is ${monthInWords(lastMonthValue())}.`}
          value={month}
          max={lastMonthValue()}
          onChange={(e) => { setMonth(e.target.value); setSaveError(null); }}
          error={saveError}
        />
        {editing && editing.history && editing.history.length > 0 && (
          <div className="cm-history">
            <div className="cm-history-title">Earlier changes</div>
            <ul>
              {editing.history.map((change) => (
                <li key={change.changed_at}>
                  {monthInWords(change.closed_through)} · {change.changed_by} · {whenInWords(change.changed_at)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </HMSModal>
    </Layout>
  );
};

export default ClosedMonthsPage;
